'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { sendSubscriptionConfirmation } from '@/lib/email/transactional';

interface MpesaCheckoutProps {
  tier: 'student' | 'undergraduate' | 'premium';
  amount: number;
  onSuccess?: (receiptNumber: string) => void;
  onCancel?: () => void;
}

const TIER_LABELS: Record<string, string> = {
  student: 'Student Plan',
  undergraduate: 'Undergraduate Plan',
  premium: 'Premium Plan',
};

const TIER_COLORS: Record<string, string> = {
  student: 'from-emerald-500 to-teal-600',
  undergraduate: 'from-blue-500 to-indigo-600',
  premium: 'from-amber-500 to-orange-600',
};

type CheckoutStep = 'form' | 'waiting' | 'success' | 'failed';

export default function MpesaCheckout({ tier, amount, onSuccess, onCancel }: MpesaCheckoutProps) {
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState<CheckoutStep>('form');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkoutRequestId, setCheckoutRequestId] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [pollCount, setPollCount] = useState(0);
  const [countdown, setCountdown] = useState(60);
  const router = useRouter();

  const pollPaymentStatus = useCallback(async () => {
    if (!checkoutRequestId) return;

    try {
      const res = await fetch(`/api/mpesa/status?checkoutRequestId=${checkoutRequestId}`);
      const data = await res.json();

      if (data.status === 'completed') {
        setReceiptNumber(data.receiptNumber || '');
        setStep('success');
        onSuccess?.(data.receiptNumber || '');

        // Send subscription confirmation email
        try {
          const { createClient } = await import('@/lib/supabase/client');
          const supabase = createClient();
          const { data: { user } } = await supabase.auth.getUser();
          if (user?.email) {
            await sendSubscriptionConfirmation({
              email: user.email,
              tier,
              receipt: data.receiptNumber || '',
              amount,
            });
          }
        } catch (emailErr) {
          console.error('Subscription confirmation email error:', emailErr);
        }

        // Redirect to confirmation page
        const params = new URLSearchParams({
          tier,
          receipt: data.receiptNumber || '',
          amount: String(amount),
        });
        router.push(`/payment-confirmation?${params.toString()}`);
        return true;
      } else if (data.status === 'failed' || data.status === 'cancelled' || data.status === 'timeout') {
        setError(data.resultDesc || 'Payment was not completed. Please try again.');
        setStep('failed');
        return true;
      }
    } catch {
      // Silently continue polling
    }
    return false;
  }, [checkoutRequestId, onSuccess, tier, amount, router]);

  useEffect(() => {
    if (step !== 'waiting' || !checkoutRequestId) return;

    let pollInterval: ReturnType<typeof setInterval>;
    let countdownInterval: ReturnType<typeof setInterval>;
    let stopped = false;

    countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    pollInterval = setInterval(async () => {
      if (stopped) return;
      setPollCount((prev) => prev + 1);
      const done = await pollPaymentStatus();
      if (done) {
        stopped = true;
        clearInterval(pollInterval);
        clearInterval(countdownInterval);
      }
    }, 4000);

    // Timeout after 60 seconds
    const timeout = setTimeout(() => {
      if (!stopped) {
        stopped = true;
        clearInterval(pollInterval);
        clearInterval(countdownInterval);
        setError('Payment timed out. Please try again.');
        setStep('failed');
      }
    }, 62000);

    return () => {
      stopped = true;
      clearInterval(pollInterval);
      clearInterval(countdownInterval);
      clearTimeout(timeout);
    };
  }, [step, checkoutRequestId, pollPaymentStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('Please enter your M-Pesa phone number.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/mpesa/stk-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), amount, tier }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to send payment request. Please try again.');
        setLoading(false);
        return;
      }

      setCheckoutRequestId(data.checkoutRequestId);
      setCountdown(60);
      setPollCount(0);
      setStep('waiting');
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setStep('form');
    setError('');
    setCheckoutRequestId('');
    setCountdown(60);
    setPollCount(0);
  };

  const gradientClass = TIER_COLORS[tier] || TIER_COLORS.premium;

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className={`bg-gradient-to-r ${gradientClass} rounded-2xl p-6 mb-6 text-white`}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <span className="text-xl">📱</span>
          </div>
          <div>
            <p className="text-white/80 text-sm font-medium">M-Pesa Payment</p>
            <p className="text-white font-700 text-lg">{TIER_LABELS[tier]}</p>
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-3xl font-800">KES {amount.toLocaleString()}</span>
          <span className="text-white/70 text-sm">/month</span>
        </div>
      </div>

      {/* Step: Form */}
      {step === 'form' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-600 text-foreground mb-2">
              M-Pesa Phone Number
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-muted-foreground text-sm font-medium">🇰🇪 +254</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="07XX XXX XXX"
                className="w-full pl-20 pr-4 py-3 border border-border rounded-xl bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                maxLength={13}
                autoComplete="tel"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              Enter the M-Pesa number registered to your Safaricom SIM
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <span className="text-red-500 text-sm mt-0.5">⚠️</span>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="bg-muted/50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-600 text-foreground">How it works:</p>
            <div className="space-y-1.5">
              {[
                'Enter your M-Pesa phone number above',
                'You\'ll receive an STK Push prompt on your phone',
                'Enter your M-Pesa PIN to confirm payment',
                'Your subscription activates instantly',
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-xs font-700 text-primary bg-primary/10 rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <p className="text-xs text-muted-foreground">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 py-3 px-4 border border-border rounded-xl text-foreground font-600 text-sm hover:bg-muted/50 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3 px-4 bg-gradient-to-r ${gradientClass} text-white font-600 text-sm rounded-xl hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <span>📲</span>
                  Pay with M-Pesa
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Step: Waiting for payment */}
      {step === 'waiting' && (
        <div className="text-center space-y-6">
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 rounded-full bg-green-100 animate-ping opacity-40" />
            <div className="relative w-24 h-24 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-4xl">📱</span>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-700 text-foreground mb-2">Check Your Phone</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              An M-Pesa payment prompt has been sent to{' '}
              <span className="font-600 text-foreground">{phone}</span>.
              <br />
              Enter your M-Pesa PIN to complete the payment.
            </p>
          </div>

          <div className="bg-muted/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Time remaining</span>
              <span className={`text-sm font-700 ${countdown <= 15 ? 'text-red-500' : 'text-foreground'}`}>
                {countdown}s
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-1000 ${countdown <= 15 ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${(countdown / 60) * 100}%` }}
              />
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 text-muted-foreground text-xs">
            <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Waiting for confirmation{pollCount > 0 ? ` (${pollCount})` : ''}...
          </div>

          <button
            onClick={handleRetry}
            className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
          >
            Didn't receive the prompt? Try again
          </button>
        </div>
      )}

      {/* Step: Success */}
      {step === 'success' && (
        <div className="text-center space-y-5">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <div>
            <h3 className="text-xl font-700 text-foreground mb-1">Payment Successful! 🎉</h3>
            <p className="text-muted-foreground text-sm">
              Your {TIER_LABELS[tier]} is now active.
            </p>
          </div>

          {receiptNumber && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-xs text-green-700 font-600 mb-1">M-Pesa Receipt Number</p>
              <p className="text-green-800 font-700 text-lg tracking-wider">{receiptNumber}</p>
              <p className="text-xs text-green-600 mt-1">Save this for your records</p>
            </div>
          )}

          <div className="bg-muted/50 rounded-xl p-4 text-left space-y-2">
            <p className="text-sm font-600 text-foreground">✅ What's unlocked:</p>
            {tier === 'student' && (
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Voice chat with Rafeeq AI</li>
                <li>• Daily motivational videos</li>
                <li>• Habit tracking & streaks</li>
                <li>• 5-language support</li>
              </ul>
            )}
            {tier === 'undergraduate' && (
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Everything in Student</li>
                <li>• Academic Research Mode</li>
                <li>• Extended conversation history</li>
                <li>• Priority AI responses</li>
              </ul>
            )}
            {tier === 'premium' && (
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Everything in Undergraduate</li>
                <li>• Career Launchpad access</li>
                <li>• ElevenLabs voice integration</li>
                <li>• Unlimited AI coaching sessions</li>
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Step: Failed */}
      {step === 'failed' && (
        <div className="text-center space-y-5">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>

          <div>
            <h3 className="text-xl font-700 text-foreground mb-1">Payment Not Completed</h3>
            <p className="text-muted-foreground text-sm">{error || 'The payment was not completed.'}</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
            <p className="text-xs font-600 text-amber-800 mb-2">Common reasons:</p>
            <ul className="text-xs text-amber-700 space-y-1">
              <li>• Wrong M-Pesa PIN entered</li>
              <li>• Insufficient M-Pesa balance</li>
              <li>• Request timed out (no response)</li>
              <li>• Request cancelled on your phone</li>
            </ul>
          </div>

          <div className="flex gap-3">
            {onCancel && (
              <button
                onClick={onCancel}
                className="flex-1 py-3 px-4 border border-border rounded-xl text-foreground font-600 text-sm hover:bg-muted/50 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleRetry}
              className={`flex-1 py-3 px-4 bg-gradient-to-r ${gradientClass} text-white font-600 text-sm rounded-xl hover:opacity-90 transition-opacity`}
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* M-Pesa branding footer */}
      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <span>🔒</span>
        <span>Secured by Safaricom M-Pesa · Lipa Na M-Pesa</span>
      </div>
    </div>
  );
}
